import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { AddCircleOutline } from "@mui/icons-material";
import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import MasterViewController from "../../redux/actions/masterApiActions";
import HolidayViewController from "../../redux/actions/HolidayApiAction";
import HolidayMasterFormModal from "./AddHolidayMaster";
import ReusableGrid from "../../components/ag grid/ReusableGrid";
import PageSizeSelector from "../../components/ag grid/PageSizeSelector";
import ExportButton from "../../components/ag grid/ExportButton";
import ListPageLayout from "../../components/layout/ListPageLayout";
import { toolbarButtonSx } from "../../components/auth/authTheme";
import usePermission from "../../utils/usePermission";

import "../../styles/styles.css";
import "../../styles/layout.css";

const EmployerHolidayMasterList = () => {
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const { hasPermission } = usePermission();

  const [showModal, setShowModal] = useState(false);
  const [selectedHolidays, setSelectedHolidays] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const { getHolidays } = MasterViewController();
  const { getEmployerHoliday } = HolidayViewController();
  const { holidays } = useSelector((state) => state.master);
  const { employer_holiday } = useSelector((state) => state.employer_holidays);

  const canCreate = hasPermission("oragnization_holiday_management", "create");
  const canExport = hasPermission("oragnization_holiday_management", "export");

  useEffect(() => {
    dispatch(getHolidays());
    dispatch(getEmployerHoliday());
  }, [dispatch]);

  const availableYears = useMemo(() => {
    const fromMaster = Object.values(holidays || {})
      .flat()
      .map((h) => h.year)
      .filter(Boolean);
    const fromEmployer = (employer_holiday || [])
      .map((item) => item.holiday?.year)
      .filter(Boolean);
    const current = new Date().getFullYear();
    return Array.from(new Set([current, ...fromMaster, ...fromEmployer])).sort(
      (a, b) => b - a
    );
  }, [holidays, employer_holiday]);

  const filteredRowData = useMemo(() => {
    const rows = employer_holiday || [];
    return rows.filter((item) => item.holiday?.year === selectedYear);
  }, [employer_holiday, selectedYear]);

  const handleOpenModal = useCallback(() => {
    if (employer_holiday?.length) {
      const alreadySelectedIds = employer_holiday.map((item) =>
        item.holiday ? item.holiday.id : item.id
      );
      setSelectedHolidays(alreadySelectedIds);
    } else {
      setSelectedHolidays([]);
    }
    setShowModal(true);
  }, [employer_holiday]);

  const columnDefs = useMemo(
    () => [
      {
        headerCheckboxSelection: true,
        checkboxSelection: true,
        width: 52,
        maxWidth: 52,
        minWidth: 52,
        pinned: "left",
        suppressSizeToFit: true,
        sortable: false,
        headerClass: "checkbox-selection-header",
        cellClass: "checkbox-selection-cell",
      },
      {
        headerName: "SR No.",
        valueGetter: (params) => params.node.rowIndex + 1,
        width: 72,
        maxWidth: 80,
        minWidth: 72,
        suppressSizeToFit: true,
      },
      {
        headerName: "Occasion",
        valueGetter: (params) => params.data.holiday?.occasion,
        flex: 2,
        minWidth: 200,
        wrapText: true,
        autoHeight: true,
        cellClass: "ag-cell-wrap-text",
        tooltipValueGetter: (params) => params.data.holiday?.occasion,
      },
      {
        headerName: "Date",
        valueGetter: (params) => params.data.holiday?.date,
        width: 118,
        maxWidth: 118,
        minWidth: 118,
        suppressSizeToFit: true,
        cellClass: "ag-cell-ellipsis",
      },
      {
        headerName: "Day",
        valueGetter: (params) => params.data.holiday?.day,
        width: 96,
        maxWidth: 96,
        minWidth: 96,
        suppressSizeToFit: true,
        cellClass: "ag-cell-ellipsis",
      },
      {
        headerName: "Year",
        valueGetter: (params) => params.data.holiday?.year,
        width: 90,
        maxWidth: 100,
        minWidth: 90,
        suppressSizeToFit: true,
      },
    ],
    []
  );

  const yearFilter = (
    <FormControl size="small" sx={{ minWidth: 110 }}>
      <InputLabel id="employer-holiday-year-label">Year</InputLabel>
      <Select
        labelId="employer-holiday-year-label"
        value={selectedYear}
        label="Year"
        onChange={(e) => setSelectedYear(Number(e.target.value))}
      >
        {availableYears.map((year) => (
          <MenuItem key={year} value={year}>
            {year}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <ListPageLayout
      title="Organization Holidays"
      subtitle="Holidays observed by your organization for the selected year"
      headerAction={
        canCreate ? (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddCircleOutline />}
            onClick={handleOpenModal}
            sx={toolbarButtonSx}
          >
            Add Holiday
          </Button>
        ) : null
      }
      toolbarRight={
        <>
          {canExport && (
            <ExportButton
              gridRef={gridRef}
              fileName="organization_holidays.xlsx"
              exportColumns={["holiday.occasion", "holiday.date", "holiday.day"]}
              disabled={selectedRows.length === 0}
            />
          )}
          {yearFilter}
          <PageSizeSelector
            pageSize={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
          />
        </>
      }
    >
      <div className="list-page-grid">
        <ReusableGrid
          columnDefs={columnDefs}
          rowData={filteredRowData}
          gridRef={gridRef}
          pagination={true}
          pageSize={pageSize}
          autoRowHeight={true}
          getRowId={(params) => String(params.data.id)}
          onSelectionChanged={(rows) => setSelectedRows(rows)}
          emptyMessage="No holidays selected"
          emptyHint={
            canCreate
              ? 'Click "Add Holiday" to choose holidays from the master list.'
              : "No organization holidays are configured for this year."
          }
        />
      </div>

      {showModal && (
        <HolidayMasterFormModal
          show={showModal}
          onHide={() => setShowModal(false)}
          selectedHolidays={selectedHolidays}
          setSelectedHolidays={setSelectedHolidays}
        />
      )}
    </ListPageLayout>
  );
};

export default EmployerHolidayMasterList;
